import React, { useMemo, useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  InputBase,
  Popover,
  Stack,
  Typography
} from "@mui/material";
import { PDFTypeIcon } from "assets/material-custom-icons";
import { BreadcrumbActions } from "components/Bread-crumbs/BreadcrumbActionsSlot";
import { useActionHook } from "hooks";
import { useAccountSelector, useClientActions, useInvestorsSelector } from "store";
import { IClientWithInvestors } from "store/types/client";

import type { BatchExportRequest } from "../context/ReportGenerator-context";

interface Props {
  onPreview: () => void;
  onExportPDF: () => void;
  onBatchExport: (request: BatchExportRequest) => void;
}

const toggleInSet = <T,>(set: Set<T>, id: T): Set<T> => {
  const next = new Set(set);
  next.has(id) ? next.delete(id) : next.add(id);
  return next;
};

// A client's investor is identified by (clientId, investorId): the SAME investor can belong to
// several clients, so selection must be keyed per client — not by investor id alone.
const clientInvKey = (clientId: number, investorId: number): string => `${clientId}:${investorId}`;

const menuItemSx = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  px: "12px",
  py: "6px",
  cursor: "pointer",
  "&:hover": { bgcolor: "#f6f8fc" }
} as const;

const checkboxSx = {
  p: 0,
  "& .MuiSvgIcon-root": { fontSize: 20 },
  "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#071f35" }
};

const rowLabelSx = { flex: 1, fontSize: 14, lineHeight: "16px", color: "#071f35", minWidth: 0 } as const;

export const HeaderActions: React.FC<Props> = ({ onPreview, onExportPDF, onBatchExport }) => {
  const { enable_client_crm, show_ai_report_generator } = useAccountSelector() || {};
  // Uploading generated PDFs to a client workspace needs BOTH flags on (and a non-viewer role, enforced server-side).
  const canSaveToWorkspace = Boolean(enable_client_crm && show_ai_report_generator);

  const { getClientsWithInvestorsAction } = useClientActions();
  const { data: clients = [], loading, action: fetchClients } = useActionHook(getClientsWithInvestorsAction);
  const accountInvestors = useInvestorsSelector();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // Kept separate so we can tell whether the account-investors section is involved (upload vs download-only).
  // Keyed by `${clientId}:${investorId}` so the same investor under different clients is tracked separately.
  const [selectedClientInvestors, setSelectedClientInvestors] = useState<Set<string>>(new Set());
  const [selectedClients, setSelectedClients] = useState<Set<number>>(new Set());
  const [selectedAccountInvestors, setSelectedAccountInvestors] = useState<Set<number>>(new Set());
  const [downloadAsPdf, setDownloadAsPdf] = useState(false);

  const openPopover = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    if (enable_client_crm) {
      fetchClients().catch((err) => console.error("[HeaderActions] failed to load clients:", err));
    }
  };
  const closePopover = () => setAnchorEl(null);

  const resetSelection = () => {
    setSelectedClientInvestors(new Set());
    setSelectedClients(new Set());
    setSelectedAccountInvestors(new Set());
    setQuery("");
  };

  const filteredClients = useMemo<IClientWithInvestors[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients
      .map((client) => {
        const nameMatch = client.name.toLowerCase().includes(q);
        const investors = nameMatch
          ? client.investors
          : client.investors.filter((inv) => inv.name.toLowerCase().includes(q));
        return nameMatch || investors.length ? { ...client, investors } : null;
      })
      .filter((client): client is IClientWithInvestors => client !== null);
  }, [clients, query]);

  const filteredAccountInvestors = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? accountInvestors.filter((inv) => inv.name.toLowerCase().includes(q)) : accountInvestors;
  }, [accountInvestors, query]);

  const isExpanded = (id: number) => (query.trim() ? true : expanded.has(id));
  const toggleExpand = (id: number) => setExpanded((prev) => toggleInSet(prev, id));

  // The parent checkbox represents the CLIENT ITSELF (a client_id-level file), independent of its investors.
  // Checked = client selected; indeterminate = client not selected but some of its investors are.
  const clientState = (client: IClientWithInvestors): { checked: boolean; indeterminate: boolean } => {
    const selected = selectedClients.has(client.id);
    const someInvestor = client.investors.some((inv) => selectedClientInvestors.has(clientInvKey(client.id, inv.id)));
    return { checked: selected, indeterminate: !selected && someInvestor };
  };

  const toggleClient = (client: IClientWithInvestors) => setSelectedClients((prev) => toggleInSet(prev, client.id));

  // Selectable units: each client itself (1) + each of its investors + each account investor.
  const { totalSelectable } = useMemo(() => {
    let count = 0;
    clients.forEach((client) => (count += 1 + client.investors.length));
    count += accountInvestors.length;
    return { totalSelectable: count };
  }, [clients, accountInvestors]);

  const totalSelected = selectedClientInvestors.size + selectedClients.size + selectedAccountInvestors.size;
  const allSelected = totalSelectable > 0 && totalSelected === totalSelectable;

  const handleSelectAll = () => {
    if (allSelected) {
      resetSelection();
      return;
    }
    const clientIds = new Set<number>();
    const clientInv = new Set<string>();
    clients.forEach((client) => {
      clientIds.add(client.id);
      client.investors.forEach((inv) => clientInv.add(clientInvKey(client.id, inv.id)));
    });
    setSelectedClients(clientIds);
    setSelectedClientInvestors(clientInv);
    setSelectedAccountInvestors(new Set(accountInvestors.map((inv) => inv.id)));
  };

  // Account investors are download-only. When any is selected, the whole export is PDF-download (no upload)
  // and the "Download as PDF" option is forced on + disabled.
  const hasAccountSelected = selectedAccountInvestors.size > 0;
  const effectiveDownload = hasAccountSelected || downloadAsPdf;

  const handleExport = () => {
    if (!totalSelected) return;
    const uploadAllowed = !hasAccountSelected && canSaveToWorkspace;
    const items: BatchExportRequest["items"] = [];

    clients.forEach((client) => {
      // Client itself selected → a client-level PDF (client_id). Independent of investor selection.
      if (selectedClients.has(client.id)) {
        items.push({
          name: client.name,
          filters: { client_id: [client.id] },
          upload: uploadAllowed ? { clientId: client.id } : undefined
        });
      }
      // Each individually-selected investor → an investor-level PDF (client_id + investor_id).
      client.investors.forEach((inv) => {
        if (selectedClientInvestors.has(clientInvKey(client.id, inv.id))) {
          items.push({
            name: inv.name,
            filters: { Investor: [inv.name] },
            upload: uploadAllowed ? { clientId: client.id, investorId: inv.id } : undefined
          });
        }
      });
    });

    accountInvestors.forEach((inv) => {
      if (selectedAccountInvestors.has(inv.id)) items.push({ name: inv.name, filters: { Investor: [inv.name] } });
    });

    // Dedupe (an investor could be both a client investor and an account investor by name).
    const seen = new Set<string>();
    const deduped = items.filter((item) => {
      const key = item.upload ? `u:${item.upload.clientId}:${item.upload.investorId ?? ""}` : `d:${item.name}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    closePopover();
    onBatchExport({ items: deduped, download: effectiveDownload });
    resetSelection();
  };

  const investorRow = (rowKey: string, name: string, checked: boolean, onToggle: () => void, indent = "40px") => (
    <Box key={rowKey} sx={{ ...menuItemSx, pl: indent }}>
      <Checkbox checked={checked} onChange={onToggle} sx={checkboxSx} />
      <Typography onClick={onToggle} sx={rowLabelSx}>
        {name}
      </Typography>
    </Box>
  );

  return (
    <BreadcrumbActions>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Button
          onClick={onPreview}
          variant="outlined"
          sx={{
            height: "24px",
            minHeight: 0,
            p: "0 14px",
            borderRadius: "999px",
            bgcolor: "#FFFFFF",
            color: "#071f35",
            fontSize: 12,
            fontWeight: 700,
            textTransform: "none",
            flexShrink: 0,
            "&:hover": { bgcolor: "#f6f8fc" }
          }}
        >
          Preview
        </Button>

        <Box sx={{ position: "relative", flexShrink: 0, display: "flex", gap: 0 }}>
          <Button
            startIcon={<PDFTypeIcon sx={{ fill: "#FFFFFF", width: 16, height: 16 }} />}
            onClick={onExportPDF}
            sx={{
              height: "24px",
              minHeight: 0,
              p: "0 14px",
              border: "none",
              borderRadius: "999px 0 0 999px",
              bgcolor: "#071f35",
              color: "#FFFFFF",
              fontSize: 12,
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "0 8px 16px rgba(7,31,53,0.2)",
              "&:hover": { bgcolor: "#0a2e4f" }
            }}
          >
            Export PDF
          </Button>

          {/* The popover always renders (Account Investors export). The clients tree inside is CRM-gated. */}
          <Button
            onClick={(e) => (open ? closePopover() : openPopover(e))}
            title="Export to client"
            sx={{
              height: "24px",
              minHeight: 0,
              p: "0 8px",
              border: "none",
              minWidth: 0,
              borderLeft: "1px solid rgba(255,255,255,0.25)",
              borderRadius: "0 999px 999px 0",
              bgcolor: "#071f35",
              color: "#FFFFFF",
              fontSize: 10,
              boxShadow: "0 8px 16px rgba(7,31,53,0.2)",
              "&:hover": { bgcolor: "#0a2e4f" }
            }}
          >
            {open ? <ArrowDropUpIcon sx={{ fontSize: 16 }} /> : <ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          </Button>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={closePopover}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  p: "24px",
                  borderRadius: "8px",
                  boxShadow: "0px 10px 15px -3px rgba(18,18,23,0.08), 0px 4px 6px -2px rgba(18,18,23,0.05)",
                  width: 380
                }
              }
            }}
          >
            <Stack sx={{ gap: "20px" }}>
              {/* Header */}
              <Stack direction="row" sx={{ gap: "6px", pb: "8px", alignItems: "flex-start" }}>
                <Stack sx={{ flex: 1, gap: "2px", minWidth: 0 }}>
                  <Typography sx={{ fontSize: 18, lineHeight: "24px", color: "#071f35" }}>Export to client</Typography>
                  <Typography sx={{ fontSize: 14, lineHeight: "16px", color: "#496682" }}>
                    Generate PDF per client or investor and save it in client&apos;s portal.
                  </Typography>
                </Stack>
                <IconButton onClick={closePopover} sx={{ p: "4px" }}>
                  <CloseIcon sx={{ fontSize: 16, color: "#071f35" }} />
                </IconButton>
              </Stack>

              <Stack sx={{ gap: "10px" }}>
                {/* Search */}
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    gap: "8px",
                    px: "8px",
                    height: "32px",
                    border: "1px solid #7b92a9",
                    borderRadius: "8px"
                  }}
                >
                  <SearchIcon sx={{ fontSize: 16, color: "#92a7bc" }} />
                  <InputBase
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search"
                    sx={{ flex: 1, fontSize: 14, color: "#071f35", "& input::placeholder": { color: "#92a7bc", opacity: 1 } }}
                  />
                </Stack>

                {/* Select all */}
                <Box sx={{ py: "8px", borderBottom: "1px solid #dbe8f5" }}>
                  <Typography
                    onClick={totalSelectable ? handleSelectAll : undefined}
                    sx={{
                      fontSize: 14,
                      lineHeight: "16px",
                      color: "#26a6ae",
                      cursor: totalSelectable ? "pointer" : "default",
                      width: "fit-content"
                    }}
                  >
                    {allSelected ? "Deselect all" : "Select all"}
                  </Typography>
                </Box>

                {/* Tree + account investors */}
                <Box sx={{ maxHeight: 260, overflowY: "auto", borderBottom: "1px solid #dbe8f5" }}>
                  {loading ? (
                    <Stack alignItems="center" sx={{ py: 3 }}>
                      <CircularProgress size={22} />
                    </Stack>
                  ) : filteredClients.length === 0 && filteredAccountInvestors.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: "#92a7bc", py: 2, textAlign: "center" }}>
                      No results found.
                    </Typography>
                  ) : (
                    <>
                      {enable_client_crm &&
                        filteredClients.map((client) => {
                          const state = clientState(client);
                          const hasInvestors = client.investors.length > 0;
                          const expandedNow = isExpanded(client.id);
                          return (
                            <Box key={client.id}>
                              <Box sx={menuItemSx}>
                                <Box
                                  sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: hasInvestors ? "pointer" : "default" }}
                                  onClick={() => hasInvestors && toggleExpand(client.id)}
                                >
                                  {hasInvestors &&
                                    (expandedNow ? (
                                      <KeyboardArrowDownIcon sx={{ fontSize: 20, color: "#5d7782" }} />
                                    ) : (
                                      <KeyboardArrowRightIcon sx={{ fontSize: 20, color: "#5d7782" }} />
                                    ))}
                                </Box>
                                <Checkbox
                                  checked={state.checked}
                                  indeterminate={state.indeterminate}
                                  onChange={() => toggleClient(client)}
                                  sx={checkboxSx}
                                />
                                <Typography onClick={() => toggleClient(client)} sx={rowLabelSx}>
                                  {client.name}
                                </Typography>
                              </Box>
                              {hasInvestors &&
                                expandedNow &&
                                client.investors.map((inv) => {
                                  const key = clientInvKey(client.id, inv.id);
                                  return investorRow(
                                    `cli-${key}`,
                                    inv.name,
                                    selectedClientInvestors.has(key),
                                    () => setSelectedClientInvestors((prev) => toggleInSet(prev, key)),
                                    "60px"
                                  );
                                })}
                            </Box>
                          );
                        })}

                      {/* Plain account investors — a non-selectable section label, then the flat list. */}
                      {filteredAccountInvestors.length > 0 && (
                        <>
                          <Box
                            sx={{
                              px: "12px",
                              py: "6px",
                              mt: enable_client_crm && filteredClients.length ? "4px" : 0,
                              borderTop: enable_client_crm && filteredClients.length ? "1px solid #dbe8f5" : "none"
                            }}
                          >
                            <Typography sx={{ fontSize: 14, lineHeight: "16px", fontWeight: 500, color: "#496682" }}>
                              Account Investors
                            </Typography>
                          </Box>
                          {filteredAccountInvestors.map((inv) =>
                            investorRow(`acc-${inv.id}`, inv.name, selectedAccountInvestors.has(inv.id), () =>
                              setSelectedAccountInvestors((prev) => toggleInSet(prev, inv.id))
                            )
                          )}
                        </>
                      )}
                    </>
                  )}
                </Box>
              </Stack>

              {/* Footer */}
              <Stack direction="row" sx={{ gap: "8px", alignItems: "center" }}>
                <Checkbox
                  checked={effectiveDownload}
                  disabled={hasAccountSelected}
                  onChange={() => setDownloadAsPdf((prev) => !prev)}
                  sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 24 }, "&.Mui-checked": { color: "#071f35" } }}
                />
                <Typography sx={{ flex: 1, fontSize: 14, lineHeight: "16px", color: "#071f35" }}>
                  Download as PDF
                </Typography>
                <Button
                  onClick={handleExport}
                  disabled={!totalSelected}
                  sx={{
                    px: "16px",
                    py: "4px",
                    minHeight: 0,
                    borderRadius: "32px",
                    bgcolor: "#071f35",
                    color: "#FFFFFF",
                    fontSize: 16,
                    lineHeight: "24px",
                    textTransform: "none",
                    "&:hover": { bgcolor: "#0a2e4f" },
                    "&.Mui-disabled": { bgcolor: "#dbe8f5", color: "#92a7bc" }
                  }}
                >
                  Export
                </Button>
              </Stack>
            </Stack>
          </Popover>
        </Box>
      </Box>
    </BreadcrumbActions>
  );
};
